import React, { useRef } from 'react';

import _ from 'lodash';
import * as Yup from 'yup';
import { Dialog, DialogContent, Grid, Typography } from '@material-ui/core';

import API from 'utils/api';
import { useSources, useValueChange } from 'hooks';
import { DialogActions, DialogTitle, Form, PasswordField } from 'components';
import { call } from 'utils';

import styles from './ChangePasswordDialog.module.scss';

const initialValues = {
  currentPassword: '',
  newPassword: '',
  newPassword2: '',
};

const validate = Yup.object().shape({
  currentPassword: Yup.string().required('Required'),
  newPassword: Yup.string()
    .required('Required')
    .min(6, 'Password must be at least 6 characters long')
    .when('currentPassword', {
      is: x => !!x,
      then: it =>
        it.notOneOf([Yup.ref('currentPassword')], 'Do not enter your current password'),
    }),
  newPassword2: Yup.string()
    .required('Required')
    .oneOf([Yup.ref('newPassword')], 'Password confirmation does not match'),
});

const ChangePasswordDialog = ({ open, onClose }) => {
  const status = useRef();
  const sources = useSources();

  useValueChange(open, () => {
    if (open) {
      status.current = undefined;
    }
  });

  const onSubmit = async (values, { setSubmitting, setFieldError }) => {
    const { data } = await API.updatePassword(
      {
        actualPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmNewPassword: values.newPassword2,
      },
      sources
    );

    status.current = undefined;

    switch (_.get(data, 'status')) {
      case 'success':
        status.current = 'done';
        break;

      case 'incorrect password':
        setFieldError('currentPassword', 'Incorrect password');
        break;

      default:
        status.current = 'error';
        break;
    }

    setSubmitting(false);
  };

  const renderDialog = ({ fieldProps, isSubmitting, resetForm, submitForm }) => {
    const onClick = () => {
      resetForm();
      call(onClose, status.current === 'done');
    };

    let message, actions;

    switch (status.current) {
      case 'done':
        message = 'Your password has been changed successfully!';
        actions = [{ label: 'login', onClick }];
        break;

      case 'error':
        message = 'Unable to update your password';
        actions = [{ label: 'close', onClick }];
        break;

      default:
        actions = [
          { label: 'cancel', onClick, disabled: isSubmitting },
          { label: 'save', onClick: submitForm, disabled: isSubmitting, type: 'submit' },
        ];

        break;
    }

    const onCloseDialog = status.current === 'done' || isSubmitting ? null : onClick;

    return (
      <Dialog open={open} onClose={onCloseDialog}>
        <DialogTitle onClose={onCloseDialog}>Change your password</DialogTitle>

        <DialogContent className={styles.content}>
          <Grid container spacing={3} className={message ? styles.hidden : ''}>
            <Grid item xs={12}>
              <PasswordField
                {...fieldProps('currentPassword', 'Your current password')}
              />
            </Grid>

            <Grid item xs={12}>
              <PasswordField {...fieldProps('newPassword', 'Your new password')} />
            </Grid>

            <Grid item xs={12}>
              <PasswordField
                {...fieldProps('newPassword2', 'Confirm your new password')}
              />
            </Grid>
          </Grid>

          {message ? (
            <div className={styles.message}>
              <Typography>{message}</Typography>
            </div>
          ) : null}
        </DialogContent>

        <DialogActions actions={actions} />
      </Dialog>
    );
  };

  return (
    <Form validationSchema={validate} initialValues={initialValues} onSubmit={onSubmit}>
      {renderDialog}
    </Form>
  );
};

export default ChangePasswordDialog;
